import { HTML5_BOILERPLATE } from './htmlBoilerplate';

/** Void / self-closing HTML tags (no closing pair). */
const VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

/**
 * Expand a simple Emmet-like abbreviation into HTML.
 * Supports: tag, tag.class, tag#id, tag.class#id, tag[attr=value]
 */
export function expandSimpleEmmet(abbr) {
  const raw = String(abbr || '').trim();
  if (!raw || raw.includes('>') || raw.includes('+') || raw.includes('*')) {
    return null;
  }

  if (raw === '!') {
    const marker = '<body>\n    ';
    return {
      text: HTML5_BOILERPLATE,
      cursorOffset: HTML5_BOILERPLATE.indexOf(marker) + marker.length,
    };
  }

  const match = raw.match(
    /^([a-zA-Z][\w-]*)((?:[.#][\w-]+)*)?(?:\[([^\]]*)\])?$/,
  );
  if (!match) return null;

  const tag = match[1].toLowerCase();
  const modifiers = match[2] || '';
  const bracketAttrs = match[3];

  const classes = [];
  let id = '';
  for (const part of modifiers.matchAll(/([.#])([\w-]+)/g)) {
    if (part[1] === '.') classes.push(part[2]);
    else id = part[2];
  }

  const attrs = [];
  if (id) attrs.push(`id="${id}"`);
  if (classes.length) attrs.push(`class="${classes.join(' ')}"`);
  if (bracketAttrs) {
    const pieces = bracketAttrs.trim().split(/\s+/).filter(Boolean);
    for (const piece of pieces) {
      if (piece.includes('=')) {
        const [k, ...rest] = piece.split('=');
        const v = rest.join('=');
        attrs.push(`${k}="${v.replace(/^["']|["']$/g, '')}"`);
      } else {
        attrs.push(piece);
      }
    }
  }

  if (tag === 'img' && !attrs.some((a) => a.startsWith('src='))) {
    attrs.push('src=""', 'alt=""');
  }
  if (tag === 'a' && !attrs.some((a) => a.startsWith('href='))) {
    attrs.push('href="#"');
  }
  if (tag === 'link' && !attrs.some((a) => a.startsWith('rel='))) {
    attrs.push('rel="stylesheet"', 'href="styles.css"');
  }
  if (tag === 'input' && !attrs.some((a) => a.startsWith('type='))) {
    attrs.push('type="text"');
  }

  const attrStr = attrs.length ? ` ${attrs.join(' ')}` : '';

  if (VOID_TAGS.has(tag)) {
    const text = `<${tag}${attrStr}>`;
    return { text, cursorOffset: text.length };
  }

  const text = `<${tag}${attrStr}></${tag}>`;
  return { text, cursorOffset: text.length - `</${tag}>`.length };
}

function resolveLanguage(getLanguageFn) {
  return String(
    typeof getLanguageFn === 'function' ? getLanguageFn() : getLanguageFn || '',
  ).toLowerCase();
}

/**
 * VS Code-style Emmet Tab expand: `!`, `h1`, `div.card`, `img`, `a`, …
 */
export function bindHtmlEmmetExpand(editor, monaco, getLanguageFn) {
  if (!editor || !monaco) return () => {};

  const disposable = editor.onKeyDown((e) => {
    if (e.keyCode !== monaco.KeyCode.Tab) return;
    if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;
    if (resolveLanguage(getLanguageFn) !== 'html') return;

    const model = editor.getModel();
    const position = editor.getPosition();
    if (!model || !position) return;

    const line = model.getLineContent(position.lineNumber);
    const before = line.slice(0, position.column - 1);
    const abbrMatch = before.match(
      /(?:^|[\s>])(!|[a-zA-Z][\w-]*(?:[.#][\w-]+)*(?:\[[^\]]*\])?)$/,
    );
    if (!abbrMatch) return;

    const abbr = abbrMatch[1];
    if (/<[a-zA-Z][^>]*$/.test(before) && abbr !== '!') return;

    const expanded = expandSimpleEmmet(abbr);
    if (!expanded) return;

    e.preventDefault();
    e.stopPropagation();

    const startColumn = position.column - abbr.length;
    const range = {
      startLineNumber: position.lineNumber,
      startColumn,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    };

    editor.pushUndoStop();
    editor.executeEdits('emmet-expand', [{ range, text: expanded.text }]);
    editor.pushUndoStop();

    const nextModel = editor.getModel();
    if (nextModel) {
      const startOffset = nextModel.getOffsetAt({
        lineNumber: position.lineNumber,
        column: startColumn,
      });
      const caret = nextModel.getPositionAt(startOffset + expanded.cursorOffset);
      editor.setPosition(caret);
      editor.revealPositionInCenterIfOutsideViewport?.(caret);
    }
  });

  return () => disposable?.dispose?.();
}

/**
 * Auto-insert closing tag when typing `>` after an opening tag.
 * Matches VS Code `html.autoClosingTags` behaviour for learners.
 */
export function bindHtmlAutoCloseTags(editor, monaco, getLanguageFn) {
  if (!editor || !monaco) return () => {};

  // Prefer Monaco's onDidType when available (fires after the char is inserted).
  const onDidType = typeof editor.onDidType === 'function' ? editor.onDidType.bind(editor) : null;

  const handleTyped = (text) => {
    if (text !== '>') return;
    if (resolveLanguage(getLanguageFn) !== 'html') return;

    const model = editor.getModel();
    const position = editor.getPosition();
    if (!model || !position) return;

    const line = model.getLineContent(position.lineNumber);
    const before = line.slice(0, position.column - 1);
    // before ends with ...<tag attrs>
    const openMatch = before.match(/<([a-zA-Z][\w-]*)([^<>]*)>$/);
    if (!openMatch) return;

    const tag = openMatch[1].toLowerCase();
    const rest = openMatch[2] || '';
    if (VOID_TAGS.has(tag)) return;
    if (/\/\s*$/.test(rest)) return;

    const after = line.slice(position.column - 1);
    if (after.startsWith(`</${tag}>`)) return;

    const insert = `</${tag}>`;
    editor.executeEdits('html-auto-close', [
      {
        range: {
          startLineNumber: position.lineNumber,
          startColumn: position.column,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        },
        text: insert,
      },
    ]);
    editor.setPosition(position);
  };

  if (onDidType) {
    const disposable = onDidType(handleTyped);
    return () => disposable?.dispose?.();
  }

  // Fallback: keydown + microtask
  const disposable = editor.onKeyDown((e) => {
    const key = e.browserEvent?.key;
    if (key !== '>') return;
    if (resolveLanguage(getLanguageFn) !== 'html') return;
    window.setTimeout(() => handleTyped('>'), 0);
  });

  return () => disposable?.dispose?.();
}
